import { Link, useNavigate } from "react-router-dom";
import { Radio, PlayCircle, Building2, ArrowRight } from "lucide-react";

const TEACHABLE_URL =
  "https://zarantech.teachable.com/l/products?sortKey=name&sortDirection=asc&page=1";

const FORMATS = [
  {
    id: "live",
    icon: Radio,
    title: "Instructor-Led Live",
    subtitle: "Guided . Real-time",
    bullets: [
      "Live sessions with instructors",
      "Trainer interaction and doubt clearing",
      "Cohort-style structured learning",
      "Best for learners who want guidance",
    ],
    accent: "var(--cyan)",
    href: "/courses",
    external: false,
  },
  {
    id: "selfpaced",
    icon: PlayCircle,
    title: "Self-Paced Video",
    subtitle: "Flexible . On your time",
    bullets: [
      "Structured recorded modules",
      "Learn anytime, from anywhere",
      "Built for working professionals",
      "Flexible pace, repeatable content",
    ],
    accent: "#A2E5FF",
    href: TEACHABLE_URL,
    external: true,
  },
  {
    id: "corporate",
    icon: Building2,
    title: "Corporate Training",
    subtitle: "Custom . Team-wide",
    bullets: [
      "Custom batches per team",
      "Curriculum mapped to roles",
      "Coordination and reporting",
      "For organizations and departments",
    ],
    accent: "#7CC4FF",
    href: "/corporate-training",
    external: false,
  },
  /* Future format: Workshops and Masterclasses. Hidden for now as per requirement.
  {
    id: "workshop",
    icon: Lightbulb,
    title: "Workshops and Masterclasses",
    subtitle: "Focused . High-impact",
    bullets: [
      "Short, focused sessions",
      "Practical demos and scenarios",
      "AI, SAP, Workday, Leadership",
      "Maximum learning in limited time",
    ],
    accent: "#FFD2A2",
    href: "/courses",
    external: false,
  },
  */
];

export default function LearningFormats() {
  const navigate = useNavigate();
  const goTo = (f) => {
    if (f.external) {
      window.open(f.href, "_blank", "noopener,noreferrer");
    } else {
      navigate(f.href);
    }
  };
  return (
    <section
      id="learning-formats"
      data-testid="learning-formats-section"
      className="py-12 lg:py-12 bg-bg-alt relative overflow-hidden"
    >
      <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
              03 . Learning formats
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-[34px] font-bold text-ink mt-3 leading-[1.1] text-balance">
              Choose the learning format that fits your pace and goal.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md border border-line bg-white px-5 py-3 text-sm font-semibold text-ink hover:border-royal hover:text-royal shadow-sm"
            >
              Not sure which one? Get Guidance <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="mt-12 space-y-3">
          {FORMATS.map((f, i) => (
            <article
              key={f.id}
              className="group grid md:grid-cols-12 items-stretch gap-0 rounded-2xl border border-line bg-white overflow-hidden hover:border-royal/40 shadow-sm hover:shadow-[0_18px_40px_-24px_rgba(15,42,115,0.32)] transition-all"
            >
              <div className="md:col-span-1 hidden md:flex items-center justify-center bg-bg-alt border-r border-line">
                <span className="font-mono-ui text-[12px] tracking-[0.2em] text-ink-soft">
                  0{i + 1}
                </span>
              </div>
              <div className="md:col-span-4 p-6 lg:p-8 flex items-start gap-4 border-b md:border-b-0 md:border-r border-line">
                <span
                  className="mt-1 inline-flex h-12 w-12 items-center justify-center rounded-xl text-royal-deep"
                  style={{ background: f.accent + "55" }}
                >
                  <f.icon size={20} />
                </span>
                <div>
                  <p className="font-mono-ui text-[10.5px] uppercase tracking-[0.22em] text-ink-soft">
                    {f.subtitle}
                  </p>
                  <h3 className="font-display text-xl lg:text-2xl font-bold text-ink mt-1 leading-tight">
                    {f.title}
                  </h3>
                </div>
              </div>
              <div className="md:col-span-6 p-6 lg:p-8 grid sm:grid-cols-2 gap-3">
                {f.bullets.map((b) => (
                  <div key={b} className="flex items-start gap-2.5 text-[13.5px] text-ink">
                    <span
                      className="mt-2 h-1 w-3 rounded-full"
                      style={{ background: "var(--royal)" }}
                      aria-hidden
                    />
                    <span className="leading-snug">{b}</span>
                  </div>
                ))}
              </div>
              <div className="md:col-span-1 flex items-center justify-center p-4 border-t md:border-t-0 md:border-l border-line">
                <button
                  type="button"
                  onClick={() => goTo(f)}
                  data-testid={`learning-format-arrow-${f.id}`}
                  aria-label={`Explore ${f.title}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-royal-deep text-white group-hover:bg-[var(--cyan)] group-hover:text-[var(--royal-deep)] transition-colors"
                >
                  <ArrowRight size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
